import { Slide, toast } from 'react-toastify';
import { renderLinksInText } from './commonUtil';
import store from '../state/store';
import {
  setActionKey,
  setFeatureFilterPayload,
  setFilteredFeatureResults,
  setMapLayerVisibility,
  updateFilteredFeatureResult
} from '../state/slices/rpcSlice';
import { setIsFilteredFeatureResultsOpen } from '../state/slices/uiSlice';
import { mergeMatchedKeys } from '../components/search/utils/SearchUtil';
import strings from '../translations';
import { updateLayers } from './rpcUtil';

export const getDescTagContent = (text, startTag, endTag) => {
  let tagContent = [];
  let index = 0;

  while (index < text.length) {
    let startPos = text.indexOf(startTag, index);
    if (startPos === -1) break;

    let endPos = text.indexOf(endTag, startPos + startTag.length);
    if (endPos === -1) break; // Added this to handle cases where the end tag is not found

    let content = text.substring(startPos + startTag.length, endPos);
    tagContent.push(content);

    index = endPos + endTag.length;
  }
  return tagContent;
};

const getPropertyOperator = (operator) => {
  switch (operator) {
    case 'equals':
      return '===';
    case 'notEquals':
      return '!==';
    case 'smallerThan':
      return '<';
    case 'biggerThan':
      return '>';
    case 'includes':
      return 'includes';
    case 'doesntInclude':
      return 'doesntInclude';
    default:
      return '===';
  }
};

// string arvot
export const getCQLStringPropertyOperator = (property, operator, value) => {
  switch (operator) {
    case 'equals':
      return (
        'strToLowerCase(' +
        property +
        ')' +
        ' = ' +
        "'" +
        value.toString().trim().toLowerCase() +
        "'"
      );
    case 'notEquals':
      return (
        'strToLowerCase(' +
        property +
        ')' +
        ' <> ' +
        "'" +
        value.toString().trim().toLowerCase() +
        "'"
      );
    case 'includes':
      return (
        'strToLowerCase(' +
        property +
        ')' +
        ' LIKE ' +
        "'%" +
        value.toString().trim().toLowerCase() +
        "%'"
      );
    case 'doesntInclude':
      return (
        'strToLowerCase(' +
        property +
        ')' +
        ' NOT LIKE ' +
        "'%" +
        value.toString().trim().toLowerCase() +
        "%'"
      );
    default:
      return property + " = '" + value.toString().trim().toLowerCase() + "'";
  }
};

// string arvot
export const getCQLDatePropertyOperator = (property, value) => {
  var options = { year: 'numeric', month: 'long', day: 'numeric' };
  if (value.start && value.end) {
    return (
      'strToLowerCase(' +
      property +
      ')' +
      ' > ' +
      "dateParse('MMM d, yyyy','" +
      value.start.toLocaleDateString('en-EN', options) +
      "')" +
      ' AND ' +
      'strToLowerCase(' +
      property +
      ')' +
      ' < ' +
      "dateParse('MMM d, yyyy','" +
      value.end.toLocaleDateString('en-EN', options) +
      "')"
    );
  } else if (value.start && !value.end) {
    return (
      'strToLowerCase(' +
      property +
      ')' +
      ' > ' +
      "dateParse('MMM d, yyyy','" +
      value.start.toLocaleDateString('en-EN', options) +
      "')"
    );
  } else if (!value.start && value.end) {
    return (
      'strToLowerCase(' +
      property +
      ')' +
      ' < ' +
      "dateParse('MMM d, yyyy','" +
      value.end.toLocaleDateString('en-EN', options) +
      "')"
    );
  }
};

// number arvot
export const getCQLNumberPropertyOperator = (property, operator, value) => {
  switch (operator) {
    case 'equals':
      return property + ' = ' + value;
    case 'notEquals':
      return property + ' <> ' + value;
    case 'smallerThan':
      return property + ' < ' + value;
    case 'biggerThan':
      return property + ' > ' + value;
    default:
      return property + ' = ' + value;
  }
};

const getParameterCaseInsensitive = (object, key) => {
  const asLowercase = key.toLowerCase();
  return object[
    Object.keys(object).filter(function (k) {
      return k.toLowerCase() === asLowercase;
    })[0]
  ];
};

const noResultsSearchNumber = (property, codeValues) => {
  var searchString = '';
  for (var i = 0; i < codeValues.length; i++) {
    if (i === 0) {
      searchString += '(';
    }
    searchString += property + ' <> ' + codeValues[i];
    if (i !== codeValues.length - 1) {
      searchString += ' AND ';
    } else {
      searchString += ')';
    }
  }
  return searchString;
};

const resultsSearchNumber = (property, codeValueKeys) => {
  var searchString = '';
  for (var i = 0; i < codeValueKeys.length; i++) {
    if (i === 0) {
      searchString += '(';
    }
    searchString += property + ' = ' + codeValueKeys[i];
    if (i !== codeValueKeys.length - 1) {
      searchString += ' OR ';
    } else {
      searchString += ')';
    }
  }
  return searchString;
};

const noResultsSearchString = (property, codeValues) => {
  var searchString = '';
  for (var i = 0; i < codeValues.length; i++) {
    if (i === 0) {
      searchString += '(';
    }
    searchString +=
      'strToLowerCase(' +
      property +
      ") <> '" +
      codeValues[i].toString().trim().toLowerCase();
    if (i !== codeValues.length - 1) {
      searchString += "' AND ";
    } else {
      searchString += "')";
    }
  }
  return searchString;
};

const resultsSearchString = (property, codeValueKeys) => {
  var searchString = '';
  for (var i = 0; i < codeValueKeys.length; i++) {
    if (i === 0) {
      searchString += '(';
    }
    searchString +=
      'strToLowerCase(' +
      property +
      ") = '" +
      codeValueKeys[i].toString().trim().toLowerCase();
    if (i !== codeValueKeys.length - 1) {
      searchString += "' OR ";
    } else {
      searchString += "')";
    }
  }
  return searchString;
};

const getCodeValuePropertyOperator = (
  property,
  operator,
  value,
  codeValues,
  filterType
) => {
  var codeValueKeys;

  switch (operator) {
    case 'equals':
      codeValueKeys = Object.keys(codeValues).filter(
        (key) =>
          codeValues[key].toString().trim().toLowerCase() ===
          value.toString().trim().toLowerCase()
      );
      break;

    case 'notEquals':
      codeValueKeys = Object.keys(codeValues).filter(
        (key) =>
          codeValues[key].toString().trim().toLowerCase() !==
          value.toString().trim().toLowerCase()
      );
      break;

    case 'includes':
      codeValueKeys = Object.keys(codeValues).filter((key) =>
        codeValues[key]
          .toString()
          .trim()
          .toLowerCase()
          .includes(value.toString().trim().toLowerCase())
      );
      break;

    case 'doesntInclude':
      codeValueKeys = Object.keys(codeValues).filter(
        (key) =>
          !codeValues[key]
            .toString()
            .trim()
            .toLowerCase()
            .includes(value.toString().trim().toLowerCase())
      );
      break;

    default:
      console.log(
        'Error filtering:',
        property,
        operator,
        value,
        codeValues,
        filterType
      );
      codeValueKeys = Object.keys(codeValues).filter(
        (key) =>
          codeValues[key].toString().trim().toLowerCase() ===
          value.toString().trim().toLowerCase()
      );
      break;
  }

  if (codeValueKeys.length === 0) {
    return filterType === 'number'
      ? noResultsSearchNumber(property, Object.keys(codeValues))
      : noResultsSearchString(property, Object.keys(codeValues));
  } else {
    // is the type number or string
    return filterType === 'number'
      ? resultsSearchNumber(property, codeValueKeys)
      : resultsSearchString(property, codeValueKeys);
  }
};

export const getPropertyOperatorCQL = (filter) => {
  // if the values are coded we need to handle it differently
  if (filter.codeValues) {
    return getCodeValuePropertyOperator(
      filter.property,
      filter.operator,
      filter.value,
      filter.codeValues,
      filter.type
    );
  } else {
    switch (filter.type) {
      case 'string':
        return getCQLStringPropertyOperator(
          filter.property,
          filter.operator,
          filter.value
        );
      case 'number':
        return getCQLNumberPropertyOperator(
          filter.property,
          filter.operator,
          filter.value
        );
      case 'date':
        return getCQLDatePropertyOperator(filter.property, filter.value);
      default:
        return getCQLStringPropertyOperator(
          filter.property,
          filter.operator,
          filter.value
        );
    }
  }
};

export const updateFiltersOnMap = (filtersString, filterInfo, channel) => {
  if (filtersString?.length > 0) {
    channel &&
      channel.postRequest('MapModulePlugin.MapLayerUpdateRequest', [
        filterInfo.layer.id,
        true,
        { CQL_FILTER: filtersString }
      ]);
  } else {
    filterInfo.layer &&
      channel &&
      channel.postRequest('MapModulePlugin.MapLayerUpdateRequest', [
        filterInfo.layer.id,
        true,
        { CQL_FILTER: null }
      ]);
  }
};

export const filterFeature = (feature, location, filters, channel) => {
  if (filters.length === 0 && filters === 0) {
    channel &&
      channel.postRequest('MapModulePlugin.MapLayerUpdateRequest', [
        location.layerId,
        true,
        { CQL_FILTER: null }
      ]);
    return true;
  }

  var comparisonOperatorsHash = {
    '<': function (a, b) {
      return a < b.value;
    },
    '>': function (a, b) {
      return a > b.value;
    },
    '>=': function (a, b) {
      return a >= b.value;
    },
    '<=': function (a, b) {
      return a <= b.value;
    },
    '==': function (a, b) {
      return a === b.value;
    },
    '!==': function (a, b) {
      return b.type === 'string' && typeof a === 'string'
        ? a.toString().trim().toLowerCase() !==
            b.value.toString().trim().toLowerCase()
        : a !== b.value;
    },
    '===': function (a, b) {
      return b.type === 'string' && typeof a === 'string'
        ? a.toString().trim().toLowerCase() ===
            b.value.toString().trim().toLowerCase()
        : a === b.value;
    },
    includes: function (a, b) {
      return a
        .toString()
        .trim()
        .toLowerCase()
        .includes(b.value.toString().trim().toLowerCase());
    },
    doesntInclude: function (a, b) {
      return !a
        .toString()
        .trim()
        .toLowerCase()
        .includes(b.value.toString().trim().toLowerCase());
    }
  };

  const properties = feature.keyValueProperties;
  const filterMatch = filters.every((filter) => {
    if (location.layerId === filter.layer && filter.type !== 'date') {
      const operator = getPropertyOperator(filter.operator);
      var comparisonOperator = comparisonOperatorsHash[operator];
      const value = getParameterCaseInsensitive(properties, filter.property);
      if (value === undefined || value === null) return false;
      const doFilter = comparisonOperator(value, filter);
      return doFilter;
    } else if (filter.type === 'date') {
      const value = new Date(
        getParameterCaseInsensitive(properties, filter.property)
      );
      const startDate = filter.value.start;
      const endDate = filter.value.end;

      var isAfterStart;
      var isBeforeEnd;

      if (startDate) {
        isAfterStart = value >= startDate;
      }
      if (endDate) {
        isBeforeEnd = value <= endDate;
      }
      return startDate && endDate
        ? isAfterStart && isBeforeEnd
        : isAfterStart || isBeforeEnd;
    } else {
      return true;
    }
  });

  return filterMatch;
};

export const handleFeatureAction = (keyValueProperties, propertyValue, layer, channel) => {
  return renderLinksInText(propertyValue, (filterData) => {
    // Empty FilteredFeatureResults so we get fresh results with every feature click
    store.dispatch(setFilteredFeatureResults([]));

    const payload = resolveFeatureActionTarget(
      filterData,
      keyValueProperties,
      layer.config
    );

    store.dispatch(setFeatureFilterPayload(payload));

    if (payload) {
      executeSearchAction(payload, channel, 0);
    } else {
      toast.error(strings.gfi.errors.handleFeatureActionError, {
        position: 'top-center',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
        transition: Slide
      });
    }
  });
};

/*
AI-GENERATED
365 Copilot
LLM: GPT-5.2
Tarkistanut Oskari Rintamäki
Date: 2026-05-04
*/

/**
 * Executes one "page" of feature searches for all layers in payload.
 * - Merges "load more" pages into existing layer results.
 * - Opens results dialog if any features were found.
 * - Shows a SINGLE generic error toast if ANY layer call fails.
 * - Does NOT expose backend error strings to the user.
 *
 * NOTE: startIndex is per-call (manual "Load more" button decides nextStartIndex).
 */
export const executeSearchAction = (payload, channel, startIndex = 0) => {
  const totalRequests = payload?.layers?.length ?? 0;

  let completedRequests = 0;
  let totalFeaturesFound = 0;

  // ✅ Track failures, but do NOT show raw error strings to the user.
  let hadAnyError = false;

  // Optional: helpful for console logging / debugging without user-facing exposure
  const failedLayerIds = [];

  const finishIfDone = () => {
    completedRequests += 1;

    if (completedRequests !== totalRequests) return;

    const hasResults = totalFeaturesFound > 0;

    // 1) Results exist -> open dialog always
    if (hasResults) {
      // We got results so open those layers
      const allLayers = store.getState().rpc.allLayers;
      const layerIds = [...new Set(store.getState().rpc.filteredFeatureResults.map(r => r?.content?.layerId).filter(Boolean))];
      const resultLayers = allLayers.filter(l => layerIds.includes(l.id));
      resultLayers.forEach(l => !l.visible && store.dispatch(setMapLayerVisibility(l)));
      updateLayers(store, channel);

      // Open popup
      store.dispatch(setIsFilteredFeatureResultsOpen(true));

      // 2) If ANY errors occurred, show ONE generic message (warning is usually best UX)
      if (hadAnyError) {
        toast.warn(
          strings?.gfi?.errors?.searchPartialGeneric ||
            'Some results could not be loaded. Showing partial results.',
          {
            position: 'top-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'colored',
            transition: Slide
          }
        );
      }
      return;
    }

    // No results:
    if (hadAnyError) {
      toast.error(
        strings?.gfi?.errors?.searchFailedGeneric ||
          'Search failed. Please try again.',
        {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'colored',
          transition: Slide
        }
      );

      // Optional dev logging (kept out of UI)
      // eslint-disable-next-line no-console
      console.warn('searchFeatures failed for layers:', failedLayerIds);
    } else {
      toast.info(strings?.gfi?.noResultsTitle || 'No results found.', {
        position: 'top-center',
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'colored',
        transition: Slide
      });
    }
  };

  payload.layers.forEach(({ layerId, filters }) => {
    channel.searchFeatures(
      [layerId, filters, startIndex],
      (data) => {
        // Treat missing geojson as "0 results" (not an error)
        if (!data?.content?.geojson) {
          finishIfDone();
          return;
        }

        // ✅ Merge old + new BEFORE dispatch (for manual "Load more")
        const state = store.getState();
        const existingLayerResult = state.rpc.filteredFeatureResults.find(
          (r) => r?.content?.layerId === layerId
        );

        const mergedData = mergeLayerResults(existingLayerResult, data);
        store.dispatch(updateFilteredFeatureResult(mergedData));

        totalFeaturesFound += data.content.geojson.features?.length ?? 0;

        finishIfDone();
      },
      (_errorString) => {
        // ✅ Ignore actual error string for UI; track only that an error happened
        hadAnyError = true;
        failedLayerIds.push(layerId);

        finishIfDone();
      }
    );
  });
};

// AI-GENERATED
// 365 Copilot | GPT-5 Chat model
// Tarkistanut Oskari Rintamäki
// Date: 2026-05-04
const mergeLayerResults = (oldData, newData) => {
  const merged = { ...newData };

  merged.content.geojson.features = [
    ...(oldData?.content?.geojson?.features ?? []),
    ...(newData?.content?.geojson?.features ?? [])
  ];

  merged.content.geojson.matchedFeatures = mergeMatchedKeys(
    oldData?.content?.geojson?.matchedFeatures ?? {},
    newData?.content?.geojson?.matchedFeatures ?? {}
  );

  return merged;
};

// Returns id of layer based on layerName
const getLayerNameId = (layerName) => {
  const allLayers = store.getState().rpc.allLayers;
  const layer = allLayers.find(l => l.layerName === layerName);
  return layer?.id ?? null;
}

// AI-GENERATED | 365 Copilot | GPT-5.2 | Tarkistanut Oskari Rintamäki | 2026-04-30
/*
  Example attributes:
  "data" :{
    "featureActions": {
      "turvalaitteet": [
        {
          "match": {
            "values": {
              "type": "alityyppi"
            },
            "source": ["jnro"]
          },
          "layer": 6775
        }
      ]
    }
  }
*/
// Resolves feature action links like:
//   turvalaitteet:type=kelluva:area=nimifi

// AI-GENERATED
// 365 Copilot | GPT-5 chat model
// Tarkistanut Oskari Rintamäki
// Date: 2026-05-04
//
// resolveFeatureActionTarget
// - Supports value-based filters even when source attributes are missing
// - Keeps existing behavior fully backward compatible
export function resolveFeatureActionTarget(
  target,
  keyValueProperties,
  layerAttributes
) {
  if (!target || !keyValueProperties || !layerAttributes) {
    return null;
  }

  const parts = target.split(':');
  const actionKey = parts.shift();

  store.dispatch(setActionKey(actionKey.charAt(0).toUpperCase() + actionKey.slice(1)))

  const actionDefs = layerAttributes?.featureActions?.[actionKey];
  if (!Array.isArray(actionDefs)) {
    return null;
  }

  // --- Parse key=value pairs from the feature link ---
  const valuePairs = {};
  parts.forEach((p) => {
    const [k, v] = p.split('=');
    if (k && v !== undefined) {
      valuePairs[k] = v;
    }
  });

  const payload = { layers: [] };

  actionDefs.forEach((def) => {
    const filters = [];

    /* ---------------------------------------------
       Source-based filters (optional)
    --------------------------------------------- */
    def.match?.source?.forEach((sourceKey) => {
      const val = keyValueProperties?.[sourceKey];
      if (val !== undefined && val !== null) {
        filters.push({
          attribute: sourceKey,
          value: val
        });
      }
    });

    /* ---------------------------------------------
       Value-based filters (independent fallback)
    --------------------------------------------- */
    Object.entries(def.match?.values || {}).forEach(
      ([valueKey, targetAttr]) => {
        const val = valuePairs[valueKey];
        if (val !== undefined) {
          filters.push({
            attribute: targetAttr,
            value: val
          });
        }
      }
    );

    // Accept the action if we have at least one filter
    if (filters.length > 0) {
      const id = getLayerNameId(def.layer);
      if (id === null) return null;
      payload.layers.push({
        layerId: id,
        filters
      });
    }
  });

  return payload.layers.length ? payload : null;
}

export const flattenFeatureForTable = (feature) => {
  // For ka-table: unwrap properties or geojson up to top-level keys
  if (feature.properties) {
    return { ...feature.properties, id: feature.id };
  }
  if (feature.geojson) {
    return { ...feature.geojson, id: feature.id };
  }
  return feature;
};

export const getColumnsFromRow = (row) => {
  if (!row) return [];
  // Remove typical non-field keys, you can expand this list as needed
  return Object.keys(row)
    .filter((k) => k !== 'id' && k !== 'UID')
    .map((key) => ({
      key,
      title: key,
      width: 180,
      colGroup: { style: { minWidth: 120 } }
    }));
};

/*
AI-GENERATED
365 Copilot
LLM: GPT-5.2
Tarkistanut Oskari Rintamäki
Date: 2026-05-04
*/

/**
 * Ensures row.id is filled from case variants (ID/Id/iD) when id is nullish.
 * Also optionally removes the duplicate "ID" key to avoid ambiguity.
 */
export function normalizeIdCase(row, { removeUppercaseId = false } = {}) {
  if (!row || typeof row !== 'object') return row;

  // If id is null/undefined but ID exists, promote it
  if (row.id == null && row.ID != null) {
    row.id = row.ID;
  }

  // Optionally remove duplicate key after normalization
  if (removeUppercaseId && row.ID !== undefined) {
    delete row.ID;
  }

  return row;
}
